<script setup lang="ts">
import type { __ImageItemFragment, __ProductListingBoxFragment } from '../generated/graphql'
import type { RouteLocationRaw } from 'vue-router'
import type { NuxtLink } from '#components'

defineOptions({ inheritAttrs: false })

export type Props = {
  toId?: None | ((i: __ProductListingBoxFragment) => string | undefined)
  classLink?: string | undefined
  sizes?: 'never'
  sizesM?: 'never'
  products: __ProductListingBoxFragment[] | None
  target?: ComponentExposed<typeof NuxtLink>['target']
  mobile: boolean
  routeTo: ((doc: { slug: string; id?: string | None }) => RouteLocationRaw | undefined) | null | undefined
}

export type SlotProps = {
  slugSet(slug: string | undefined): void
  imgSet(img: __ImageItemFragment | None): void
  elemImg(): HTMLElement | undefined
}

const t23 = useT2Base({
  'Limited at': 'Ribota',
  'Available at': 'Prieinama',
  Soon: 'Netrukus',
  'Sold out': 'Išparduota',
  New: 'Nauja',
  'Shop off': 'Parduotuvė uždaryta',
  'Ends in': 'Baigiasi už',
})

const props = defineProps<Props>()
const css = useCssModule()
const inited = useDelayInit(1000)

const data = shallowReactive({
  active: undefined as undefined | string,
  saved: undefined as
    | undefined
    | {
        id?: string
        elem?: undefined | HTMLElement
        img?: __ImageItemFragment
        slug?: undefined | string
      },
})
const SAVE_EMPTY: typeof data.saved = { elem: undefined, id: undefined, img: undefined, slug: undefined }

function add(ad: Partial<typeof data.saved>) {
  data.saved = { ...data.saved, ...ad }
}
const updaters: SlotProps = {
  elemImg: () => data.saved?.elem,
  imgSet: (img: __ImageItemFragment) => img?.url !== data.saved?.img?.url && add({ img }),
  slugSet: (i) => i !== data.saved?.slug && add({ slug: i }),
}

const timer = useTimeout()
function mouseenter2(e: MouseEvent | TouchEvent, id: string | None, on: boolean) {
  if (on) {
    timer.set(() => {
      const el = helpers.getHTMLElement(e.currentTarget) || helpers.getHTMLElement(e.target)
      const elem = el?.querySelector<HTMLElement>('._img') ?? el
      add({ ...SAVE_EMPTY, elem, id: id || undefined })
      data.active = id || undefined
    }, 40)
  } else if (id === data.active) {
    timer.clear()
  }
}
const list = computed(() => {
  const { routeTo, mobile, classLink, toId } = props
  const i18n = {
    availableAt: t23('Available at'),
    limited: t23('Limited at'),
    soon: t23('Soon'),
    soo: t23('Sold out'),
    new: t23('New'),
    end: t23('Ends in'),
  }

  return {
    i18n,
    class: {
      box: ['_relative _h100p _w100p d_wmax500 _hidden', mobile ? css.m : css.d],
      link: [css.link, classLink],
      title: [css.title, mobile ? '_flexblock _pb1 _ctrY' : '_ellipsis2'],
      p1: mobile ? '_block _pl04 _fw-l' : undefined,
      t: mobile ? '_block _pl04' : undefined,
      img: mobile ? '' : '_br14',
    },
    sizes: (mobile && props.sizesM) || props.sizes || (mobile ? ('50vw' as const) : ('25vw' as const)),
    items: helpers.arr(props.products, (p, index) => {
      return {
        active: false,
        id: toId ? toId(p) : undefined,
        lazy: index > 3,
        endAt: p._values?._uiPrice?.endAt,
        priceOld: p._values?._uiPrice?.priceOld?.priceForHumans,
        product: p,
        image: getListingImg(p),
        to: routeTo?.(p),
        availableAt: p.availableAt ? `${i18n.availableAt} ${helpers.uiDate(p.availableAt, 'm:d')}` : undefined,
        quantityLimit: p.quantityLimit ? `${i18n.limited} ${p.quantityLimit}` : undefined,
        stockMsg: !p?._values || p?._values?.inStock ? undefined : p?.settingsQty?.stockMsg === 'Soon' ? i18n.soon : i18n.soo,
        updaters,
        listeners: !mobile
          ? {
              onMouseenterPassive: (e: MouseEvent) => mouseenter2(e, p.id, true),
              // onMouseleavePassive: (e: MouseEvent) => mouseenter2(e, p.id, false),
            }
          : undefined,
        //  {   onTouchstart: (e: TouchEvent) => mouseenter2(e, p.id, true),   },
      }
    }),
  }
})

const list2 = computed((): ((typeof list.value.items)[0] & { active?: boolean })[] => {
  const items = list.value.items
  const active = data.active
  const saved = data.saved?.id === active ? data.saved : undefined
  if (!active) return items
  return helpers.arr(items, (i) => {
    if (active !== i.product.id) return i
    return {
      ...i,
      active: true,
      to: saved?.slug && props.routeTo ? props.routeTo({ slug: saved?.slug, id: saved.id }) : i.to,
      image: saved?.img || i.image,
    }
  })
})

export type UiActiveItem = (typeof list2.value)[0]

//
</script>

<template>
  <div v-for="ui in list2" v-bind="ui.listeners" :id="ui.id" :key="ui.product.id" v-memo="[ui.active, ui.product, ui.image, ui.to, ui.id]" :class="list.class.box">
    <NuxtLink :class="list.class.link" :target="target" :to="ui.to" :data-product="classLink ? ui.product.slug : undefined">
      <BaseImage :img="ui.image" :lazy="ui.lazy" :alt="ui.product.title" ratio="square" sizes="fixed:860" :class="list.class.img" autoplay>
        <span v-if="ui.quantityLimit || ui.availableAt" :class="$style.at">
          <span v-if="ui.quantityLimit" :class="$style.availableAt">{{ ui.quantityLimit }}</span>
          <span v-if="ui.availableAt" :class="$style.upper" class="_fs09 _fw-l _txt_end">{{ ui.availableAt }}</span>
        </span>
        <span v-if="ui.stockMsg" :class="$style.out">{{ ui.stockMsg }}</span>
        <span v-if="ui.product.isNew" :class="$style.new">{{ list.i18n.new }}</span>
      </BaseImage>

      <span class="_col">
        <span class="_ff3 _fs08 _fw-xl _mta" :class="[list.class.t, $style.ltx]">{{ ui.product.departmentName }}</span>
        <span :class="[list.class.title, list.class.t]">{{ ui.product.title }}</span>
      </span>

      <span class="_row _wrap _ctry _gap1">
        <b class="_mono _fs09" :class="[list.class.p1, ui.priceOld && '_red']">{{ ui.product._values?._uiPrice?.price?.priceForHumans }}</b>

        <s v-if="ui.priceOld" class="_mono _fs09" :class="$style.p2">{{ ui.priceOld }}</s>
        <span v-if="ui.endAt" class="_relative _gap06 _row _inline _ctrY">
          <!-- <UiMsg v-if="ui.amount" :txt="ui.amount" /> -->
          <LazyProductEndsIn v-if="inited && ui.endAt" :end-at="ui.endAt" />
        </span>
      </span>
    </NuxtLink>

    <slot name="box" :ui :active="ui.active" />
  </div>
</template>

<style module>
.link {
  composes: global(_grid);
  composes: global(_pb3);
  composes: global(_c1);

  align-items: flex-end;
  grid-template: 1fr var(--lineG) var(--line2) / 1fr;

  line-height: var(--line2);
  cursor: pointer;
}
.d .link {
  --lineG: 90px;
  --line2: 18px;
}
.m .link {
  --lineG: 116px;
  --line2: 20px;
  grid-template: 1fr var(--lineG) calc(var(--line2) * 2) / 1fr;
}
.link:focus {
  outline: none;
}
.ltx {
  composes: global(_ellipsis);
  composes: global(_inline);
  line-height: var(--line2);
  min-height: var(--line2);
}
.upper {
  composes: global(_noselect);
  composes: global(_txt_ctr);
  composes: global(_bg0);
  composes: global(_pl2);
  composes: global(_pr2);
  composes: global(_pt04);
  composes: global(_pb04);
  composes: global(_fw-m);
  composes: global(txt_upper);
  outline: none;
  z-index: 2;
}

.new,
.out {
  composes: ltx;
  composes: global(_absolute);
  composes: upper;
  font-style: italic;
}

.new {
  composes: global(_fs08);
  composes: global(_ls-xxxxl);
  top: 22px;
  transform: translateX(-100%) rotateZ(-90deg);
  transform-origin: 100% 0;
}
.m .new {
  left: 0px;
}
.d .new {
  left: 4px;
}
.out {
  composes: global(_fs1);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.at {
  composes: global(_col);
  composes: global(_absolute);
  bottom: 12px;
  right: 0px;
}

.title {
  composes: global(_ls-xs);
  composes: global(_fw-l);
  composes: global(_pr04);
  composes: global(_wmin0);
  composes: global(_hidden);
  composes: global(m_fw-l);
  composes: global(_lh11);
  composes: global(m_fs09);
  min-height: var(--line2);
}
</style>
